// src/components/DocumentEditorAIGuideline.jsx
// Dashboard panel for editing Document Editor AI prompt instructions.
// Two prompt types: "inline" (selection editing) and "document" (whole-doc generation/edit).
import React, { useState, useEffect } from 'react';
import { useClass } from '../utils/ClassContext';
import { apiCall } from '../utils/api/ApiUtils';

// ── Default prompts — these are the COMPLETE instructions the AI receives.
// Nothing is hidden in the backend code when a guideline is saved.
// ─────────────────────────────────────────────────────────────────────────────

const DEFAULT_INLINE = `You are editing a selected portion of text within a document.
- Focus on enhancing the selected text while maintaining consistency with the overall document.
- Preserve the tone and style of the original text unless explicitly asked to change it.
- Make minimal changes that directly address the user's request.
- Return ONLY the edited text without explanations unless asked.

IMPORTANT: When writing MATLAB/Octave code blocks:
- Use \`\`\`matlab:runnable ONLY if the code produces output that students need to see/verify (plots, calculations, results displayed with disp() or fprintf())
- Use \`\`\`matlab for code snippets that demonstrate syntax, define functions, or show code structure without expected output
- Examples of when to use \`\`\`matlab:runnable:
  * Code that creates plots or visualizations
  * Code that performs calculations and displays results
  * Code that demonstrates observable behavior
- Examples of when to use \`\`\`matlab:
  * Function definitions without execution
  * Syntax examples without output
  * Code snippets showing structure or patterns
  * Variable declarations or assignments without output

Example with output (use matlab:runnable):
\`\`\`matlab:runnable
x = 1:10;
plot(x, x.^2);
title('Quadratic Function');
\`\`\`

Example without output (use matlab):
\`\`\`matlab
function y = square(x)
    y = x.^2;
end
\`\`\`

IMPORTANT: When writing Python code blocks:
- Use \`\`\`python:runnable ONLY if the code produces output that students need to see/verify (plots, calculations, print() statements)
- Use \`\`\`python for code snippets that demonstrate syntax, define functions, or show code structure without expected output
- Python code runs in Pyodide (browser-based Python 3.11 with WebAssembly)
- Available packages: numpy, matplotlib, pandas, scipy, scikit-learn
- Examples of when to use \`\`\`python:runnable:
  * Code that creates matplotlib plots or visualizations
  * Code that performs calculations with NumPy and prints results
  * Code that trains ML models with scikit-learn and shows output
  * Code that demonstrates observable behavior with print statements
- Examples of when to use \`\`\`python:
  * Function or class definitions without execution
  * Syntax examples without output
  * Code snippets showing structure or patterns
  * Import statements or variable declarations without output

Example with output (use python:runnable):
\`\`\`python:runnable
import numpy as np
import matplotlib.pyplot as plt

x = np.linspace(0, 10, 100)
y = np.sin(x)
plt.plot(x, y)
plt.title('Sine Wave')
plt.show()
\`\`\`

Example without output (use python):
\`\`\`python
import numpy as np

def compute_mean(data):
    return np.mean(data)
\`\`\`

Note: For matplotlib plots, always call plt.show() to display the figure. The system will automatically capture and render the plot as SVG.

IMPORTANT: When the user requests the same example in MORE THAN ONE language (e.g., "show this in both Python and MATLAB"), emit one fenced block per language using the normal \`language:runnable\` tag, placed BACK-TO-BACK with NO prose, headings, lists, or blank-line-separated text between them.

Rules:
- Each block must use its own \`language:runnable\` fence (e.g. \`\`\`python:runnable and \`\`\`matlab:runnable). Do NOT invent a combined fence.
- Place the blocks immediately one after the other. A single blank line between fences is fine; any non-code content between them will split the tab group.
- The blocks should solve the SAME task so users can compare languages side-by-side via the tabs.
- If the user asked for only one language, do NOT add extra languages.

IMPORTANT: When illustrating Entity-Relationship Diagram (ERD) or Enhanced ERD (EERD) concepts, use the built-in ERD component — NOT MATLAB, Python, or any other code.

Use a fenced block with the tag \`erd\` or \`erd:editor\` (the editor tag makes it interactive for students):

\`\`\`erd:editor
[Entity]
EntityName
--
AttributeName
AnotherAttr @key

[Relationship]
RelationshipName
--
EntityA N
EntityB 1

[Specialization]
ParentEntity
--
Child1
Child2
\`\`\`

Supported notation rules:
- [Entity] block: entity name, then --, then attributes. Mark keys with @key, partial keys @partial_key, multivalued @multivalued, derived @derived, composite AttrName(sub1, sub2), weak entity @weak.
- [Relationship] block: relationship name, then --, then participants as "EntityName cardinality" (1, N, or M), then optionally -- for relationship attributes. Identifying relationship: add @identifying.
- [Specialization] block: parent entity name, then --, then child entity names. Total: @total, disjoint: @disjoint.
- [Union] block: for union/category types.
Use erd:editor for editable examples. Use erd for read-only reference diagrams.
NEVER use MATLAB or Python to visualize ERD/EERD concepts.

For mathematical expressions, use ONLY these LaTeX formats:
- Inline math: $expression$ (single dollar signs)
- Display math: $$expression$$ (double dollar signs)
- DO NOT use \\( \\) or \\[ \\] syntax
- IMPORTANT: When outputting JSON, all backslashes in LaTeX must be properly escaped (use \\\\ for each \\)

Use markdown formatting with proper structure:
- Use # for headings (# H1, ## H2, ### H3, etc.)
- Use **bold** and *italic* for emphasis
- Use lists with - or 1. for structure
- Use > for blockquotes`;

const DEFAULT_DOCUMENT = `You are creating or editing an educational document.
- Create comprehensive, well-structured content with clear headings and subheadings.
- Use academic writing style appropriate for the course level.
- Ensure factual accuracy and include relevant examples.
- Maintain consistency with the existing document tone and structure.
- Follow the course-specific terminology and conventions defined below (if any).

IMPORTANT: When writing MATLAB/Octave code blocks:
- Use \`\`\`matlab:runnable ONLY if the code produces output that students need to see/verify (plots, calculations, results displayed with disp() or fprintf())
- Use \`\`\`matlab for code snippets that demonstrate syntax, define functions, or show code structure without expected output
- Examples of when to use \`\`\`matlab:runnable:
  * Code that creates plots or visualizations
  * Code that performs calculations and displays results
  * Code that demonstrates observable behavior
- Examples of when to use \`\`\`matlab:
  * Function definitions without execution
  * Syntax examples without output
  * Code snippets showing structure or patterns
  * Variable declarations or assignments without output

Example with output (use matlab:runnable):
\`\`\`matlab:runnable
x = 1:10;
plot(x, x.^2);
title('Quadratic Function');
\`\`\`

Example without output (use matlab):
\`\`\`matlab
function y = square(x)
    y = x.^2;
end
\`\`\`

IMPORTANT: When writing Python code blocks:
- Use \`\`\`python:runnable ONLY if the code produces output that students need to see/verify (plots, calculations, print() statements)
- Use \`\`\`python for code snippets that demonstrate syntax, define functions, or show code structure without expected output
- Python code runs in Pyodide (browser-based Python 3.11 with WebAssembly)
- Available packages: numpy, matplotlib, pandas, scipy, scikit-learn
- Examples of when to use \`\`\`python:runnable:
  * Code that creates matplotlib plots or visualizations
  * Code that performs calculations with NumPy and prints results
  * Code that trains ML models with scikit-learn and shows output
  * Code that demonstrates observable behavior with print statements
- Examples of when to use \`\`\`python:
  * Function or class definitions without execution
  * Syntax examples without output
  * Code snippets showing structure or patterns
  * Import statements or variable declarations without output

Example with output (use python:runnable):
\`\`\`python:runnable
import numpy as np
import matplotlib.pyplot as plt

x = np.linspace(0, 10, 100)
y = np.sin(x)
plt.plot(x, y)
plt.title('Sine Wave')
plt.show()
\`\`\`

Example without output (use python):
\`\`\`python
import numpy as np

def compute_mean(data):
    return np.mean(data)
\`\`\`

Note: For matplotlib plots, always call plt.show() to display the figure. The system will automatically capture and render the plot as SVG.

IMPORTANT: When the user requests the same example in MORE THAN ONE language (e.g., "show this in both Python and MATLAB"), emit one fenced block per language using the normal \`language:runnable\` tag, placed BACK-TO-BACK with NO prose, headings, lists, or blank-line-separated text between them.

Rules:
- Each block must use its own \`language:runnable\` fence (e.g. \`\`\`python:runnable and \`\`\`matlab:runnable). Do NOT invent a combined fence.
- Place the blocks immediately one after the other. A single blank line between fences is fine; any non-code content between them will split the tab group.
- The blocks should solve the SAME task so users can compare languages side-by-side via the tabs.
- If the user asked for only one language, do NOT add extra languages.

IMPORTANT: When illustrating Entity-Relationship Diagram (ERD) or Enhanced ERD (EERD) concepts, use the built-in ERD component — NOT MATLAB, Python, or any other code.

Use a fenced block with the tag \`erd\` or \`erd:editor\` (the editor tag makes it interactive for students):

\`\`\`erd:editor
[Entity]
EntityName
--
AttributeName
AnotherAttr @key

[Relationship]
RelationshipName
--
EntityA N
EntityB 1

[Specialization]
ParentEntity
--
Child1
Child2
\`\`\`

Supported notation rules:
- [Entity] block: entity name, then --, then attributes. Mark keys with @key, partial keys @partial_key, multivalued @multivalued, derived @derived, composite AttrName(sub1, sub2), weak entity @weak.
- [Relationship] block: relationship name, then --, then participants as "EntityName cardinality" (1, N, or M), then optionally -- for relationship attributes. Identifying relationship: add @identifying.
- [Specialization] block: parent entity name, then --, then child entity names. Total: @total, disjoint: @disjoint.
- [Union] block: for union/category types.
Use erd:editor for editable examples. Use erd for read-only reference diagrams.
NEVER use MATLAB or Python to visualize ERD/EERD concepts.

For mathematical expressions, use ONLY these LaTeX formats:
- Inline math: $expression$ (single dollar signs)
- Display math: $$expression$$ (double dollar signs)
- DO NOT use \\( \\) or \\[ \\] syntax
- IMPORTANT: When outputting JSON, all backslashes in LaTeX must be properly escaped (use \\\\ for each \\)

Use markdown formatting with proper structure:
- Use # for headings (# H1, ## H2, ### H3, etc.)
- Use **bold** and *italic* for emphasis
- Use lists with - or 1. for structure
- Use > for blockquotes

When creating embedded questions in documents, use these formats:

**Multiple Choice Questions (MCQ):**
\`\`\`question:mcq
ID: Q1
TYPE: single
LO: Learning objective description
QUESTION:
Question text here (can use $latex$ for math)
OPTIONS:
A: First option
B: Second option
C: Third option
D: Fourth option
RUBRIC: B
EXPLANATION:
Explanation of correct answer
\`\`\`

**Short Answer Questions:**
\`\`\`question:short
ID: Q2
LO: Learning objective description
QUESTION:
Question text here (can use $latex$ for math)
RUBRIC:
Expected answer or key points
\`\`\`

Notes:
- ID: Unique identifier (e.g., Q1, Q2, Q3)
- TYPE: "single" for single choice, "multiple" for multiple correct answers
- LO: Learning objective (optional but recommended)
- RUBRIC: For MCQ use letter(s) of correct answer (e.g., "A" or "A,B"). For short answer, provide expected answer
- EXPLANATION: Optional detailed explanation for MCQ questions
- Questions support LaTeX math expressions using $inline$ or $$display$$ syntax`;

// ── Helpers ───────────────────────────────────────────────────────────────────
function parseContent(raw) {
  if (!raw) return { inline: DEFAULT_INLINE, document: DEFAULT_DOCUMENT };
  try {
    const parsed = JSON.parse(raw);
    return {
      inline: parsed.inline ?? DEFAULT_INLINE,
      document: parsed.document ?? DEFAULT_DOCUMENT,
    };
  } catch {
    // Legacy: plain text stored in the old single-field format — put it in document
    return { inline: DEFAULT_INLINE, document: raw };
  }
}

// ── Component ─────────────────────────────────────────────────────────────────
const DocumentEditorAIGuideline = ({ userRole }) => {
  const { getClassContext } = useClass();
  const [inline, setInline] = useState(DEFAULT_INLINE);
  const [document, setDocument] = useState(DEFAULT_DOCUMENT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('inline');

  useEffect(() => {
    const classContext = getClassContext();
    if (!classContext?.class_id) { setLoading(false); return; }
    setLoading(true);
    apiCall('/document-editor/ai-guideline?component=document_editor', { method: 'GET' }, classContext)
      .then(data => {
        const parsed = parseContent(data.guideline?.content);
        setInline(parsed.inline);
        setDocument(parsed.document);
      })
      .catch(err => setError(`Failed to load: ${err.message}`))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    const classContext = getClassContext();
    if (!classContext?.class_id) return;
    setSaving(true);
    setError(null);
    try {
      const content = JSON.stringify({ inline, document });
      await apiCall('/document-editor/ai-guideline', {
        method: 'POST',
        body: JSON.stringify({ component: 'document_editor', title: 'Document Editor AI Guideline', content }),
      }, classContext);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(`Failed to save: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const tabStyle = (id) => ({
    padding: '8px 20px',
    border: 'none',
    borderBottom: activeTab === id ? '2px solid #4f46e5' : '2px solid transparent',
    background: 'none',
    color: activeTab === id ? '#4f46e5' : '#64748b',
    fontWeight: activeTab === id ? 700 : 500,
    fontSize: 14,
    cursor: 'pointer',
    transition: 'all 0.15s',
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '32px 40px', maxWidth: 960, margin: '0 auto', width: '100%' }}>
      {/* Header */}
      <div style={{ marginBottom: 8 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: '#1e293b', margin: 0 }}>Document Editor AI Guideline</h1>
        <p style={{ color: '#64748b', marginTop: 6, fontSize: 14 }}>
          Custom instructions injected into AI calls inside the document editor.
          Edits here apply to your whole class — no per-environment setup needed.
        </p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', marginBottom: 16 }}>
        <button style={tabStyle('inline')} onClick={() => setActiveTab('inline')}>
          ✏️ Inline Editing
        </button>
        <button style={tabStyle('document')} onClick={() => setActiveTab('document')}>
          📄 Whole Document
        </button>
      </div>

      {/* Tab description */}
      {activeTab === 'inline' ? (
        <p style={{ fontSize: 13, color: '#64748b', marginBottom: 12, marginTop: 0 }}>
          Used when a student <strong>selects text</strong> and asks the AI to rewrite, improve, or expand it.
          The AI receives these rules plus the selected passage and the user's instruction.
        </p>
      ) : (
        <p style={{ fontSize: 13, color: '#64748b', marginBottom: 12, marginTop: 0 }}>
          Used when the AI <strong>generates or edits the entire document</strong> — e.g. "write a document about X" or "improve this document".
          The AI receives these rules plus the full document content and the user's request.
        </p>
      )}

      {/* Editor */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {loading ? (
          <p style={{ color: '#94a3b8' }}>Loading…</p>
        ) : activeTab === 'inline' ? (
          <textarea
            key="inline"
            value={inline}
            onChange={e => setInline(e.target.value)}
            style={textareaStyle}
            onFocus={e => { e.target.style.border = '1px solid #6366f1'; e.target.style.background = '#fff'; }}
            onBlur={e => { e.target.style.border = '1px solid #e2e8f0'; e.target.style.background = '#f8fafc'; }}
          />
        ) : (
          <textarea
            key="document"
            value={document}
            onChange={e => setDocument(e.target.value)}
            style={textareaStyle}
            onFocus={e => { e.target.style.border = '1px solid #6366f1'; e.target.style.background = '#fff'; }}
            onBlur={e => { e.target.style.border = '1px solid #e2e8f0'; e.target.style.background = '#f8fafc'; }}
          />
        )}
      </div>

      {/* Footer */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
        <button
          onClick={handleSave}
          disabled={saving || loading}
          style={{
            padding: '10px 24px',
            background: saved ? '#22c55e' : '#4f46e5',
            color: '#fff',
            border: 'none',
            borderRadius: 7,
            cursor: saving ? 'not-allowed' : 'pointer',
            fontWeight: 600,
            fontSize: 14,
            transition: 'background 0.2s',
            opacity: saving ? 0.7 : 1,
          }}
        >
          {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save Guideline'}
        </button>
        <button
          onClick={() => {
            if (activeTab === 'inline') setInline(DEFAULT_INLINE);
            else setDocument(DEFAULT_DOCUMENT);
          }}
          disabled={saving || loading}
          style={{
            padding: '10px 18px',
            background: 'none',
            color: '#64748b',
            border: '1px solid #e2e8f0',
            borderRadius: 7,
            cursor: 'pointer',
            fontWeight: 500,
            fontSize: 14,
          }}
        >
          Reset to Defaults
        </button>
        {error && <span style={{ color: '#ef4444', fontSize: 13 }}>{error}</span>}
        {!error && (
          <span style={{ color: '#94a3b8', fontSize: 12 }}>
            Inline: {inline.length} chars · Document: {document.length} chars
          </span>
        )}
      </div>
    </div>
  );
};

const textareaStyle = {
  flex: 1,
  width: '100%',
  minHeight: 420,
  fontFamily: 'monospace',
  fontSize: 13,
  lineHeight: 1.7,
  padding: '14px 16px',
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  resize: 'vertical',
  boxSizing: 'border-box',
  color: '#1e293b',
  background: '#f8fafc',
  outline: 'none',
};

export default DocumentEditorAIGuideline;
